import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid,
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, CircularProgress,
  IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tabs, Tab, Divider,
} from '@mui/material';
import {
  Refresh, GppBad, Warning, CheckCircle, Block,
  VisibilityOff, AccessTime, OpenInNew, LockPerson,
  WifiOff, Tune, LockOpen,
} from '@mui/icons-material';
import api from '../../services/api';

interface FraudStats {
  openBySeveridade: { severidade: string; total: number }[];
  openByTipo: { tipo: string; total: number }[];
  topFlaggedUsers: { user_id: string; EMAIL: string; USERNAME: string; alertas: number; ultimo_alerta: string }[];
  alertasUltimas24h: number;
}

interface FraudAlert {
  id: number;
  user_id: string;
  user_email: string;
  user_username: string;
  pagamento_id: string | null;
  referencia: string | null;
  valor: number;
  tipo: string;
  severidade: string;
  estado: string;
  detalhes: any;
  notas: string | null;
  created_at: string;
  revisado_por_email: string | null;
  revisado_em: string | null;
}

interface BlockedAccount {
  USER_ID: string;
  MOTIVO: string;
  STRIKES: number;
  DT_BLOQUEIO: string;
}

interface BlockedIp {
  IP_ADDRESS: string;
  MOTIVO: string;
  FAILS: number;
  DT_BLOQUEIO: string;
  DT_EXPIRACAO: string | null;
}

interface FraudPolicy {
  POLICY_KEY: string;
  POLICY_VALUE: string;
  DESCRICAO: string;
}

const SEVERIDADE_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  CRITICAL: 'error', HIGH: 'error', MEDIUM: 'warning', LOW: 'info',
};

const TIPO_LABEL: Record<string, string> = {
  VELOCITY_HOUR: 'Velocidade/hora',
  VELOCITY_DAY: 'Velocidade/dia',
  AMOUNT_SPIKE: 'Valor elevado',
  CUMULATIVE_DAY: 'Acumulado/dia',
  DUPLICATE_TXN: 'Duplicado',
  FAILED_RATIO: 'Falhas excessivas',
  AMOUNT_EXCEEDED_PREAUTH: 'Limite excedido (pré-auth)',
  CARD_TESTING_ECM: 'Card testing ECM',
};

const POLICY_LABEL: Record<string, string> = {
  AMOUNT_STRIKES_TO_BLOCK: 'Strikes para bloquear conta',
  IP_CARD_TESTING_FAILS: 'Falhas ECM para bloquear IP',
  IP_BLOCK_HOURS: 'Duração do bloqueio de IP (horas)',
};

const FraudAlerts: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // ── Alerts tab ──────────────────────────────────────────────────────────────
  const [stats, setStats] = useState<FraudStats | null>(null);
  const [alerts, setAlerts] = useState<FraudAlert[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState('OPEN');
  const [filterSeveridade, setFilterSeveridade] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [triageDialog, setTriageDialog] = useState<{ alert: FraudAlert; estado: string } | null>(null);
  const [triageNotes, setTriageNotes] = useState('');
  const [triaging, setTriaging] = useState(false);

  // ── Blocked tab ─────────────────────────────────────────────────────────────
  const [blockedAccounts, setBlockedAccounts] = useState<BlockedAccount[]>([]);
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  // ── Policies tab ─────────────────────────────────────────────────────────────
  const [policies, setPolicies] = useState<FraudPolicy[]>([]);
  const [policyEdits, setPolicyEdits] = useState<Record<string, string>>({});
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [savingPolicies, setSavingPolicies] = useState(false);

  // ── Shared ──────────────────────────────────────────────────────────────────
  const [alertMsg, setAlertMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/fraud/alerts/stats');
      if (res.data.success) setStats(res.data.data);
    } catch {}
  }, []);

  const fetchAlerts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(p), limit: '25' };
      if (filterEstado) params.estado = filterEstado;
      if (filterSeveridade) params.severidade = filterSeveridade;
      if (filterTipo) params.tipo = filterTipo;
      const res = await api.get('/admin/fraud/alerts', { params });
      if (res.data.success) {
        setAlerts(res.data.data);
        setTotal(res.data.pagination.total);
        setTotalPages(res.data.pagination.totalPages);
        setPage(p);
      }
    } catch {
      setAlertMsg({ type: 'error', msg: 'Erro ao carregar alertas' });
    } finally {
      setLoading(false);
    }
  }, [filterEstado, filterSeveridade, filterTipo]);

  const fetchBlocked = useCallback(async () => {
    setBlockedLoading(true);
    try {
      const res = await api.get('/admin/fraud/blocked');
      if (res.data.success) {
        setBlockedAccounts(res.data.data.accounts || []);
        setBlockedIps(res.data.data.ips || []);
      }
    } catch {
      setAlertMsg({ type: 'error', msg: 'Erro ao carregar bloqueados' });
    } finally {
      setBlockedLoading(false);
    }
  }, []);

  const fetchPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    try {
      const res = await api.get('/admin/fraud/policies');
      if (res.data.success) {
        setPolicies(res.data.data);
        const edits: Record<string, string> = {};
        res.data.data.forEach((p: FraudPolicy) => { edits[p.POLICY_KEY] = p.POLICY_VALUE; });
        setPolicyEdits(edits);
      }
    } catch {} finally { setPoliciesLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); fetchAlerts(1); }, []);
  useEffect(() => { fetchAlerts(1); }, [filterEstado, filterSeveridade, filterTipo]);
  useEffect(() => { if (activeTab === 1) fetchBlocked(); }, [activeTab]);
  useEffect(() => { if (activeTab === 2) fetchPolicies(); }, [activeTab]);

  const handleTriage = async () => {
    if (!triageDialog) return;
    setTriaging(true);
    try {
      const res = await api.put(`/admin/fraud/alerts/${triageDialog.alert.id}`, {
        estado: triageDialog.estado, notas: triageNotes || undefined,
      });
      if (res.data.success) {
        setAlertMsg({ type: 'success', msg: res.data.message });
        setTriageDialog(null);
        setTriageNotes('');
        fetchAlerts(page);
        fetchStats();
      }
    } catch (e: any) {
      setAlertMsg({ type: 'error', msg: e.response?.data?.error || 'Erro ao actualizar' });
    } finally { setTriaging(false); }
  };

  const handleUnblockAccount = async (userId: string) => {
    setUnblocking(userId);
    try {
      await api.post(`/admin/fraud/blocked/account/${userId}/unblock`);
      setAlertMsg({ type: 'success', msg: 'Conta desbloqueada' });
      fetchBlocked();
    } catch (e: any) {
      setAlertMsg({ type: 'error', msg: e.response?.data?.error || 'Erro ao desbloquear' });
    } finally { setUnblocking(null); }
  };

  const handleUnblockIp = async (ip: string) => {
    setUnblocking(ip);
    try {
      await api.post('/admin/fraud/blocked/ip/unblock', { ip });
      setAlertMsg({ type: 'success', msg: `IP ${ip} desbloqueado` });
      fetchBlocked();
    } catch (e: any) {
      setAlertMsg({ type: 'error', msg: e.response?.data?.error || 'Erro ao desbloquear IP' });
    } finally { setUnblocking(null); }
  };

  const handleSavePolicies = async () => {
    setSavingPolicies(true);
    try {
      await api.put('/admin/fraud/policies', policyEdits);
      setAlertMsg({ type: 'success', msg: 'Políticas guardadas' });
      fetchPolicies();
    } catch (e: any) {
      setAlertMsg({ type: 'error', msg: e.response?.data?.error || 'Erro ao guardar' });
    } finally { setSavingPolicies(false); }
  };

  const openCount = (sev: string) =>
    stats?.openBySeveridade.find(s => s.severidade === sev)?.total || 0;

  const fmtDate = (d: string) =>
    new Date(d).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Segurança &amp; Fraude</Typography>
          <Typography variant="body1" color="text.secondary">
            Detecção automática, bloqueios e políticas
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />}
          onClick={() => { fetchStats(); if (activeTab === 0) fetchAlerts(1); else if (activeTab === 1) fetchBlocked(); else fetchPolicies(); }}
          disabled={loading || blockedLoading}>
          Atualizar
        </Button>
      </Box>

      {alertMsg && (
        <Alert severity={alertMsg.type} sx={{ mb: 3 }} onClose={() => setAlertMsg(null)}>
          {alertMsg.msg}
        </Alert>
      )}

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Críticos (abertos)', value: openCount('CRITICAL'), color: 'error.main', icon: <GppBad /> },
          { label: 'Altos (abertos)', value: openCount('HIGH'), color: 'error.light', icon: <Warning /> },
          { label: 'Médios (abertos)', value: openCount('MEDIUM'), color: 'warning.main', icon: <Warning /> },
          { label: 'Últimas 24h', value: stats?.alertasUltimas24h ?? '—', color: 'info.main', icon: <AccessTime /> },
        ].map((s, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 2 }}>
          <Tab icon={<GppBad />} iconPosition="start" label="Alertas" />
          <Tab icon={<LockPerson />} iconPosition="start" label={`Bloqueados${blockedAccounts.length + blockedIps.length > 0 ? ` (${blockedAccounts.length + blockedIps.length})` : ''}`} />
          <Tab icon={<Tune />} iconPosition="start" label="Políticas" />
        </Tabs>
        <Divider />

        {/* ── Tab 0: Alertas ──────────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Estado</InputLabel>
                <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
                  <MenuItem value="OPEN">Aberto</MenuItem>
                  <MenuItem value="REVIEWED">Revisto</MenuItem>
                  <MenuItem value="FALSE_POSITIVE">Falso Positivo</MenuItem>
                  <MenuItem value="ACTIONED">Accionado</MenuItem>
                  <MenuItem value="">Todos</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Severidade</InputLabel>
                <Select value={filterSeveridade} label="Severidade" onChange={e => setFilterSeveridade(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="CRITICAL">Crítico</MenuItem>
                  <MenuItem value="HIGH">Alto</MenuItem>
                  <MenuItem value="MEDIUM">Médio</MenuItem>
                  <MenuItem value="LOW">Baixo</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Tipo</InputLabel>
                <Select value={filterTipo} label="Tipo" onChange={e => setFilterTipo(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(TIPO_LABEL).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {total} alerta{total !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : alerts.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600}>Nenhum alerta</Typography>
                <Typography variant="body2" color="text.secondary">Não existem alertas com os filtros seleccionados.</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Severidade</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Utilizador</TableCell>
                        <TableCell>Valor</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Acções</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.map(a => (
                        <TableRow key={a.id} hover>
                          <TableCell>
                            <Chip label={a.severidade} size="small"
                              color={SEVERIDADE_COLOR[a.severidade] || 'default'}
                              variant={a.severidade === 'CRITICAL' ? 'filled' : 'outlined'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{TIPO_LABEL[a.tipo] || a.tipo}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{a.user_username || a.user_id || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{a.user_email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{a.valor ? `${Number(a.valor).toLocaleString('pt-CV')} CVE` : '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{fmtDate(a.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={a.estado} size="small"
                              color={a.estado === 'OPEN' ? 'warning' : a.estado === 'ACTIONED' ? 'error' : 'default'}
                              variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Revisto">
                                <IconButton size="small" color="success" onClick={() => { setTriageDialog({ alert: a, estado: 'REVIEWED' }); setTriageNotes(''); }}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Falso positivo">
                                <IconButton size="small" onClick={() => { setTriageDialog({ alert: a, estado: 'FALSE_POSITIVE' }); setTriageNotes(''); }}>
                                  <VisibilityOff fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Accionado">
                                <IconButton size="small" color="error" onClick={() => { setTriageDialog({ alert: a, estado: 'ACTIONED' }); setTriageNotes(''); }}>
                                  <Block fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {a.pagamento_id && (
                                <Tooltip title="Ver pagamento">
                                  <IconButton size="small" onClick={() => window.open(`/payments?id=${a.pagamento_id}`, '_blank')}>
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Button size="small" disabled={page <= 1} onClick={() => fetchAlerts(page - 1)}>Anterior</Button>
                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page} / {totalPages}</Typography>
                    <Button size="small" disabled={page >= totalPages} onClick={() => fetchAlerts(page + 1)}>Seguinte</Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* ── Tab 1: Bloqueados ───────────────────────────────────────────────── */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            {blockedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <>
                {/* Blocked accounts */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockPerson fontSize="small" color="error" /> Contas bloqueadas ({blockedAccounts.length})
                </Typography>
                {blockedAccounts.length === 0 ? (
                  <Alert severity="success" sx={{ mb: 3 }}>Nenhuma conta bloqueada actualmente.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User ID</TableCell>
                          <TableCell>Motivo</TableCell>
                          <TableCell>Strikes</TableCell>
                          <TableCell>Bloqueado em</TableCell>
                          <TableCell align="center">Acção</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blockedAccounts.map(b => (
                          <TableRow key={b.USER_ID} hover>
                            <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{b.USER_ID}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{b.MOTIVO}</Typography></TableCell>
                            <TableCell><Chip label={b.STRIKES} size="small" color="error" /></TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{fmtDate(b.DT_BLOQUEIO)}</Typography></TableCell>
                            <TableCell align="center">
                              <Tooltip title="Desbloquear conta">
                                <IconButton size="small" color="success"
                                  disabled={unblocking === b.USER_ID}
                                  onClick={() => handleUnblockAccount(b.USER_ID)}>
                                  {unblocking === b.USER_ID ? <CircularProgress size={16} /> : <LockOpen fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Blocked IPs */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WifiOff fontSize="small" color="warning" /> IPs bloqueados ({blockedIps.length})
                </Typography>
                {blockedIps.length === 0 ? (
                  <Alert severity="success">Nenhum IP bloqueado actualmente.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Endereço IP</TableCell>
                          <TableCell>Motivo</TableCell>
                          <TableCell>Falhas</TableCell>
                          <TableCell>Bloqueado em</TableCell>
                          <TableCell>Expira em</TableCell>
                          <TableCell align="center">Acção</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blockedIps.map(b => (
                          <TableRow key={b.IP_ADDRESS} hover>
                            <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{b.IP_ADDRESS}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{b.MOTIVO}</Typography></TableCell>
                            <TableCell><Chip label={b.FAILS} size="small" color="warning" /></TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{fmtDate(b.DT_BLOQUEIO)}</Typography></TableCell>
                            <TableCell>
                              <Typography variant="body2" color={b.DT_EXPIRACAO ? 'text.secondary' : 'error.main'}>
                                {b.DT_EXPIRACAO ? fmtDate(b.DT_EXPIRACAO) : 'Permanente'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Desbloquear IP">
                                <IconButton size="small" color="success"
                                  disabled={unblocking === b.IP_ADDRESS}
                                  onClick={() => handleUnblockIp(b.IP_ADDRESS)}>
                                  {unblocking === b.IP_ADDRESS ? <CircularProgress size={16} /> : <LockOpen fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}

        {/* ── Tab 2: Políticas ────────────────────────────────────────────────── */}
        {activeTab === 2 && (
          <Box sx={{ p: 3, maxWidth: 600 }}>
            {policiesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Limiares do sistema de detecção de fraude. Alterações têm efeito imediato nas próximas transacções.
                </Typography>
                {policies.map(p => (
                  <Box key={p.POLICY_KEY} sx={{ mb: 3 }}>
                    <TextField
                      label={POLICY_LABEL[p.POLICY_KEY] || p.POLICY_KEY}
                      helperText={p.DESCRICAO}
                      type="number"
                      value={policyEdits[p.POLICY_KEY] ?? p.POLICY_VALUE}
                      onChange={e => setPolicyEdits(prev => ({ ...prev, [p.POLICY_KEY]: e.target.value }))}
                      inputProps={{ min: 0, step: 1 }}
                      fullWidth
                      size="small"
                    />
                  </Box>
                ))}
                {policies.length === 0 && (
                  <Alert severity="info">
                    As políticas são criadas automaticamente na primeira transacção processada pelo sistema de fraude.
                  </Alert>
                )}
                {policies.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleSavePolicies}
                    disabled={savingPolicies}
                    startIcon={savingPolicies ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {savingPolicies ? 'A guardar...' : 'Guardar políticas'}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Triage Dialog */}
      <Dialog open={!!triageDialog} onClose={() => setTriageDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Marcar como {triageDialog?.estado === 'REVIEWED' ? 'Revisto' : triageDialog?.estado === 'FALSE_POSITIVE' ? 'Falso Positivo' : 'Accionado'}
        </DialogTitle>
        <DialogContent>
          {triageDialog && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Alerta #{triageDialog.alert.id} — {TIPO_LABEL[triageDialog.alert.tipo] || triageDialog.alert.tipo} — {triageDialog.alert.user_username || triageDialog.alert.user_id}
              </Typography>
              <TextField
                label="Notas (opcional)"
                multiline rows={3} fullWidth
                value={triageNotes}
                onChange={e => setTriageNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriageDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={triageDialog?.estado === 'ACTIONED' ? 'error' : 'primary'}
            onClick={handleTriage}
            disabled={triaging}
            startIcon={triaging ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FraudAlerts;
